import React, { useState } from 'react';
import StepType from './calculator/StepType';
import StepLocation from './calculator/StepLocation';
import StepExtras from './calculator/StepExtras';
import StepLead from './calculator/StepLead';
import StepResult from './calculator/StepResult';
import SafeIcon from '../common/SafeIcon';
import { calculatePrice } from '../utils/pricingLogic';
import supabase from '../supabase/supabase';
import emailjs from 'emailjs-com';

export default function PricingWidget() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    type: '',
    language: 'nl',
    pax: 10,
    duration: '',
    location: '',
    distance: '',
    insightsProfiles: '',
    insightsWorkbooks: false,
    insightsBlocks: false,
    talentTest: '',
    gameModule: '',
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const updateData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const skipExtras = formData.type !== 'insights' && formData.type !== 'talenten' && formData.type !== 'game';

  const submitLead = async () => {
    setIsSubmitting(true);
    const result = calculatePrice(formData);
    
    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone || 'Niet opgegeven',
      company: formData.company || 'Niet opgegeven',
      workshop_type: formData.type,
      language: formData.language,
      participants: parseInt(formData.pax) || 0,
      duration: formData.duration,
      location: formData.location === 'wetteren' ? 'Wetteren' : 'Inhouse',
      total_price: result.total,
      price_details: result.breakdown.map(b => `${b.label}: €${b.amount}`).join(' | '),
      message: formData.message || 'Lead ingediend via prijscalculator'
    };

    try {
      const { error: supabaseError } = await supabase
        .from('workshop_submissions_20240522')
        .insert([payload]);

      if (supabaseError) console.error('Supabase Error:', supabaseError);

      if (import.meta.env.VITE_EMAILJS_SERVICE_ID) {
        await emailjs.send(
          import.meta.env.VITE_EMAILJS_SERVICE_ID,
          import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
          { ...payload, total_price: `€ ${payload.total_price}` },
          import.meta.env.VITE_EMAILJS_PUBLIC_KEY
        );
      }
      setStep(5);
    } catch (err) {
      console.error('General Submission Error:', err);
      setStep(5);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (step === 2 && skipExtras) {
      setStep(4);
    } else if (step === 4) {
      submitLead();
    } else {
      setStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handlePrev = () => {
    if (step === 4 && skipExtras) {
      setStep(2);
    } else {
      setStep(prev => Math.max(prev - 1, 1));
    }
  };

  const totalSteps = 4;
  const currentProgress = Math.min((step / totalSteps) * 100, 100);

  return (
    <div className="max-w-2xl mx-auto rounded-2xl shadow-xl overflow-hidden border border-gray-100 bg-white">
      {step < 5 && (
        <div className="border-b border-gray-100 p-6 bg-gray-50/50">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon name="Calculator" className="text-[#154A48]" />
            Bereken uw investering
          </h2>
          <div className="mt-6 h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#154A48] transition-all duration-500 ease-out"
              style={{ width: `${currentProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      <div className="p-6 sm:p-8 min-h-[400px] relative">
        {step === 1 && <StepType data={formData} updateData={updateData} onNext={handleNext} />}
        {step === 2 && <StepLocation data={formData} updateData={updateData} onNext={handleNext} onPrev={handlePrev} />}
        {step === 3 && <StepExtras data={formData} updateData={updateData} onNext={handleNext} onPrev={handlePrev} />}
        {step === 4 && <StepLead data={formData} updateData={updateData} onNext={handleNext} onPrev={handlePrev} isSubmitting={isSubmitting} />}
        {step === 5 && <StepResult data={formData} />}
      </div>
    </div>
  );
}